import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ShieldCheck, CheckCircle2 } from "lucide-react";
import { DocUpload } from "@/components/DocUpload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FIELD = { license_front: "license_front_path", license_back: "license_back_path",
  insurance_card: "insurance_card_path", selfie: "selfie_path", additional_driver: "additional_driver_license_path" };

export default function UploadDocs() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [paths, setPaths] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/reservations/docs/${token}`).then((r) => setInfo(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "This link is invalid."));
  }, [token]);

  const submit = async () => {
    const body = {};
    for (const d of info.needed) if (paths[d.key]) body[FIELD[d.key]] = paths[d.key];
    if (!Object.keys(body).length) return toast.error("Please upload at least one document.");
    setBusy(true);
    try {
      await axios.post(`${API}/reservations/docs/${token}`, body);
      setDone(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save — try again."); }
    finally { setBusy(false); }
  };

  if (err) return <div className="min-h-screen bg-black flex items-center justify-center px-4 text-white/60 text-sm">{err}</div>;
  if (!info) return <div className="min-h-screen bg-black flex items-center justify-center"><Loader2 className="animate-spin text-[#F2B705]" size={22} /></div>;
  if (done) return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4">
      <div data-testid="docs-upload-success" className="max-w-md text-center">
        <CheckCircle2 size={52} className="text-green-400 mx-auto mb-4" />
        <h1 className="font-display font-black text-3xl text-white">Documents Received!</h1>
        <p className="text-white/60 mt-3 text-sm">Thanks {info.name} — our team will continue reviewing your reservation request and contact you shortly.</p>
        <Link to="/" className="inline-block mt-6 px-6 py-2.5 rounded-md bg-[#F2B705] text-black font-bold text-sm">Back to Home</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-black py-12 px-4">
      <div className="max-w-lg mx-auto" data-testid="upload-docs-page">
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck size={22} className="text-[#F2B705]" />
          <h1 className="font-display font-black text-2xl text-white">Secure Document Upload</h1>
        </div>
        <p className="text-white/50 text-sm mb-6">Hi {info.name} — please upload the following so we can finish reviewing your reservation request. Your documents are stored securely and only visible to AutoHire management.</p>
        <div className="space-y-3">
          {info.needed.map((d) => (
            <DocUpload key={d.key} label={d.label} value={paths[d.key] || ""} testid={`upload-${d.key}`}
              onChange={(p) => setPaths((x) => ({ ...x, [d.key]: p }))} />
          ))}
        </div>
        <button data-testid="docs-submit-btn" onClick={submit} disabled={busy}
          className="mt-6 w-full flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-[#F2B705] text-black font-bold text-sm disabled:opacity-50">
          {busy && <Loader2 size={15} className="animate-spin" />} Submit Documents
        </button>
      </div>
    </div>
  );
}
